import { Paper, Table, TableBody, TableContainer, TableHead, TablePagination } from "@mui/material";
import type { ManagementImpactKind } from "components/AdvanceManagement/ManagementPreviewModal";
import EmptyState from "components/shared/EmptyState";
import type { LastElementTableAPIData } from "components/shared/LastElementTable/type";
import Pagination from "components/shared/Pagination";
import { isBookPublisher } from "helpers/publisher";
import { useCompanySettings } from "hooks/useCompanySettings";
import { useSelectRows } from "hooks/useSelectRow";
import type { Dispatch, SetStateAction } from "react";
import { useEffect, useMemo } from "react";
import { fetchAdvanceCustomStatuses } from "store/advanceCustomStatusStore";
import type { IAdvanceSlice } from "store/advancePaymentSlice";
import type { IBeneficiaryPayment } from "store/paymentSlice";
import { EPaymentResourceType, fetchResourcePayment } from "store/paymentSlice";
import { useAppDispatch, useAppSelector } from "store/store";

import variables from "../../../assets/styles/_variables.module.scss";
import TableBodyRow from "./TableBodyRow";
import TableHeadRow from "./TableHeadRow";
import TableSkeletonRows from "components/shared/TableSkeletonRows";

interface IAdvancePaymentsTable {
    slices: IAdvanceSlice[];
    APIData: LastElementTableAPIData;
    useStateFunctions: {
        setCurrentSlice: Dispatch<SetStateAction<IAdvanceSlice | null>>;
        setResourcePayment: Dispatch<SetStateAction<IBeneficiaryPayment | undefined>>;
        setLinkedAdvancePayment: Dispatch<SetStateAction<IAdvanceSlice | undefined>>;
        setSelectedRows: Dispatch<SetStateAction<number[]>>;
        setModalType: Dispatch<SetStateAction<"" | "delete" | "add" | "payment" | "import" | "update">>;
        setIsDrawerOpen: Dispatch<SetStateAction<boolean>>;
        setCreationType: Dispatch<SetStateAction<undefined | "advance" | "package">>;
        setIsConnected: Dispatch<SetStateAction<boolean>>;
    };
    handleOpenPaymentModal: (slice?: IAdvanceSlice) => void;
    handleOpenManagementPreviewModal: (slice: IAdvanceSlice, impactKind: ManagementImpactKind) => void;
    useStateData: {
        currentSlice: IAdvanceSlice | null;
        selectedRows: number[];
        creationType: undefined | "advance" | "package";
    };
    isLoading: boolean;
    emptyStateType: "advancePayment" | "advancePaid" | "advanceToPay" | "advanceUnpaid";
    onEmptyStateControlChange: (value: "all" | "paid" | "topay" | "unpaid") => void;
}

/**
 * Advance payments table as seen on Advance Management page.
 * Ideally, we would use the LastElementTable here... one day.
 */
const AdvancePaymentsTable = ({
    slices,
    APIData,
    useStateFunctions,
    handleOpenPaymentModal,
    handleOpenManagementPreviewModal,
    useStateData,
    isLoading,
    emptyStateType,
    onEmptyStateControlChange,
}: IAdvancePaymentsTable) => {
    const dispatch = useAppDispatch();
    const { companyInfos } = useCompanySettings();
    const bookPublisher = isBookPublisher(companyInfos?.publisherType);
    const customStatuses = useAppSelector((state) => state.advanceCustomStatuses.items);
    const customStatusFetchStatus = useAppSelector((state) => state.advanceCustomStatuses.status);

    useEffect(() => {
        if (!bookPublisher || customStatusFetchStatus !== "idle") return;
        void dispatch(fetchAdvanceCustomStatuses());
    }, [dispatch, customStatusFetchStatus, bookPublisher]);

    const customStatusById = useMemo(
        () => new Map(customStatuses.map((status) => [status.id, status.name])),
        [customStatuses],
    );
    const showCustomStatusColumn = customStatuses.length > 0;

    const {
        handleChangeLimit,
        handleChangePage,
        limitPagination,
        page,
        count,
        handleQuery,
        sortOrder,
        handleSort,
        sortBy,
    } = APIData;
    const {
        setCurrentSlice,
        setLinkedAdvancePayment,
        setResourcePayment,
        setSelectedRows,
        setModalType,
        setIsDrawerOpen,
        setCreationType,
        setIsConnected,
    } = useStateFunctions;
    const { currentSlice, selectedRows } = useStateData;

    const { allSelected, setAllSelected, handleSelectRow } = useSelectRows({
        setSelectedRows,
        data: slices,
    });

    const handleSetAllSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.checked) {
            setAllSelected(true);
            setSelectedRows(slices.map((slice) => slice.id));
        } else {
            setAllSelected(false);
            setSelectedRows([]);
        }
    };

    const selectedData = { allSelected, setAllSelected, handleSetAllSelected, selectedRows, handleSelectRow };

    /**
     * Opens delete modal and fetches payment linked to slice.
     */
    const handleOpenDeleteModal = async () => {
        if (!currentSlice) return;
        await dispatch(
            fetchResourcePayment({
                relatedResourceId: currentSlice.id,
                resourceType: EPaymentResourceType.ADVANCE_PAYMENT,
            }),
        ).then((res) => {
            if (res.meta.requestStatus === "fulfilled") {
                setResourcePayment(res.payload[0]);
            }
        });
        setModalType("delete");
    };

    const handleDataForUpdate = () => {
        if (!currentSlice) return;
        const allSlices = slices.filter((item) => item.paymentId === currentSlice.paymentId);
        const advancePayment = { ...currentSlice };
        advancePayment["slices"] = allSlices;
        setLinkedAdvancePayment(advancePayment);
    };

    const handleOpenUpdateModal = async ({ type }: { type: "advance" | "package" }) => {
        if (!currentSlice) return;
        await handleDataForUpdate();
        setCreationType(type);
        setModalType("update");
        setIsConnected(!!currentSlice.connectedTPD);
    };

    const tableBodyFunctions = {
        handleOpenDeleteModal,
        handleOpenPaymentModal,
        handleOpenUpdateModal,
        setIsDrawerOpen,
        handleOpenManagementPreviewModal,
    };

    return (
        <Paper
            sx={{
                padding: 0,
                marginBottom: "20px",
                border: `1px solid ${variables["border-grey"]}`,
                borderRadius: "3px",
                boxShadow: "none",
            }}
        >
            <TableContainer>
                <Table style={{ borderCollapse: "separate" }}>
                    <TableHead>
                        <TableHeadRow
                            selectedData={selectedData}
                            APIData={APIData}
                            showCustomStatusColumn={showCustomStatusColumn}
                        />
                    </TableHead>
                    {(isLoading || slices.length > 0) && (
                        <TableBody>
                            {isLoading ? (
                                <TableSkeletonRows
                                    rowCount={limitPagination}
                                    columnCount={showCustomStatusColumn ? 12 : 11}
                                    withCheckboxColumn
                                    checkboxColumnMinWidth="36px"
                                />
                            ) : (
                                slices.map((slice, i) => (
                                    <TableBodyRow
                                        key={`slice-${slice.id}`}
                                        slice={slice}
                                        index={i}
                                        allSlices={slices}
                                        tableBodyFunctions={tableBodyFunctions}
                                        selectedData={selectedData}
                                        APIData={APIData}
                                        currentSlice={currentSlice}
                                        setCurrentSlice={setCurrentSlice}
                                        showCustomStatusColumn={showCustomStatusColumn}
                                        customStatusById={customStatusById}
                                    />
                                ))
                            )}
                        </TableBody>
                    )}
                </Table>
            </TableContainer>
            {!isLoading && slices.length === 0 && (
                <div className="advance-payments-table-empty">
                    <EmptyState
                        type={emptyStateType}
                        onControlChange={(value) => {
                            if (value === "valid" || value === "invalid") return;
                            onEmptyStateControlChange(value);
                        }}
                    />
                </div>
            )}
            <TablePagination
                component={() => (
                    <Pagination
                        rowsPerPageOptions={[10, 25, 100]}
                        rowsPerPage={limitPagination}
                        page={page}
                        currentPageSize={slices.length}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeLimit}
                        type="payment"
                        count={count}
                        sticky
                    />
                )}
                count={-1}
                rowsPerPage={limitPagination}
                page={page}
                onPageChange={handleChangePage}
            />
        </Paper>
    );
};

export default AdvancePaymentsTable;
